// =====================================================================
// src/components/intl-acca/HonestEmptyState.tsx
// =====================================================================
// The "engine-validation default" empty state. Used wherever the
// backend has returned a structurally-correct response with no settled
// rows yet (PRD §9: CLV truth requires real Pinnacle closes).
//
// Three variants cover every empty surface in International Acca:
//   - "no-signals":    PRE_TOURNAMENT or no live picks today
//   - "no-clv-yet":    legs exist but Pinnacle close has not flowed
//   - "tournament-empty": tournament has no slips at all (admin-side)

interface HonestEmptyStateProps {
  variant: "no-signals" | "no-clv-yet" | "tournament-empty" | "custom";
  /** Override the default title for variant="custom". */
  title?: string;
  /** Override the default body for variant="custom". */
  body?: string;
  /** Optional CTA action — rendered as a ghost button. */
  actionLabel?: string;
  onAction?: () => void;
}

const COPY: Record<
  HonestEmptyStateProps["variant"],
  { eyebrow: string; title: string; body: string } | null
> = {
  "no-signals": {
    eyebrow: "No validated signal yet",
    title: "Holding the line.",
    body:
      "The pricing engine is running, but no fixture in the current window has cleared the confidence threshold. STRIKZA publishes when there is signal — never when there isn't.",
  },
  "no-clv-yet": {
    eyebrow: "Awaiting close",
    title: "CLV settles at kickoff.",
    body:
      "The closing-line benchmark for this leg has not posted yet. CLV columns will populate when the Pinnacle close flows — never reconstructed after the fact (PRD §12).",
  },
  "tournament-empty": {
    eyebrow: "No slips issued",
    title: "This tournament has not generated signal.",
    body:
      "Slips publish during the ACTIVE lifecycle state once the engine clears confidence + adversarial review. Empty by design when nothing meets the bar.",
  },
  custom: null,
};

export function HonestEmptyState({
  variant,
  title,
  body,
  actionLabel,
  onAction,
}: HonestEmptyStateProps): JSX.Element {
  const copy = variant === "custom"
    ? { eyebrow: "No data", title: title ?? "Nothing to show.", body: body ?? "" }
    : COPY[variant]!;

  return (
    <div className="ia-empty" role="status" aria-live="polite">
      <div className="ia-empty-eyebrow">{copy.eyebrow}</div>
      <h3 className="ia-empty-title">{copy.title}</h3>
      <p className="ia-empty-body">{copy.body}</p>
      {actionLabel && onAction ? (
        <button
          type="button"
          onClick={onAction}
          style={{
            marginTop: 16,
            background: "transparent",
            color: "var(--text)",
            border: "1px solid var(--border-strong)",
            borderRadius: "var(--sz-radius-md)",
            padding: "10px 16px",
            fontFamily: "var(--sz-font-mono)",
            fontSize: 11,
            letterSpacing: "0.18em",
            textTransform: "uppercase",
            cursor: "pointer",
          }}
        >
          {actionLabel}
        </button>
      ) : null}
    </div>
  );
}
